import React, { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import { useAuth } from "../contexts/AuthContext";
import { api } from "../lib/api";
import { useBidStream } from "../hooks/useBidStream";
import {
  Users, Building2, FileText, Gavel, ShieldCheck, Crown, Star, Globe2,
  CheckCircle2, XCircle, Bell, Activity,
} from "lucide-react";
import { toast } from "sonner";

function StatTile({ label, value, hint, icon }) {
  return (
    <div className="bg-[#0a0a0a] border border-[#262626] rounded-xl p-5">
      <div className="flex items-center justify-between">
        <p className="overline">{label}</p>
        <span className="text-brand">{icon}</span>
      </div>
      <p className="font-display text-3xl font-black text-white mt-2">{value}</p>
      {hint && <p className="text-xs text-[#737373] mt-1 font-mono">{hint}</p>}
    </div>
  );
}

function PendingDmcRow({ dmc, onVerify, onReject }) {
  return (
    <tr className="border-t border-[#1a1a1a] hover:bg-[#0c0c0c]" data-testid={`pending-dmc-${dmc.user_id}`}>
      <td className="py-4 px-4">
        <div className="font-display text-white font-bold">{dmc.name}</div>
        <div className="text-xs text-[#737373] font-mono">{dmc.email}</div>
      </td>
      <td className="py-4 px-4 text-sm text-[#a3a3a3]">{dmc.country || "—"}</td>
      <td className="py-4 px-4 text-sm text-[#a3a3a3]">
        <div className="flex flex-wrap gap-1">
          {(dmc.specialties || []).slice(0, 3).map((s) => (
            <span key={s} className="text-[10px] font-mono uppercase tracking-wider border border-[#262626] px-1.5 py-0.5">{s}</span>
          ))}
        </div>
      </td>
      <td className="py-4 px-4 text-sm font-mono text-[#737373]">
        {dmc.created_at ? new Date(dmc.created_at).toLocaleDateString() : "—"}
      </td>
      <td className="py-4 px-4">
        <div className="flex items-center gap-2 justify-end">
          <button
            onClick={() => onVerify(dmc)}
            className="inline-flex items-center gap-1.5 bg-brand text-white text-xs font-bold uppercase tracking-widest px-3 py-1.5 rounded-md hover:bg-[#FF5722]"
            data-testid={`verify-dmc-${dmc.user_id}`}
          >
            <CheckCircle2 className="w-3.5 h-3.5" /> Approve
          </button>
          <button
            onClick={() => onReject(dmc)}
            className="inline-flex items-center gap-1.5 bg-transparent border border-[#262626] text-[#a3a3a3] text-xs font-bold uppercase tracking-widest px-3 py-1.5 rounded-md hover:border-red-400 hover:text-red-400"
            data-testid={`reject-dmc-${dmc.user_id}`}
          >
            <XCircle className="w-3.5 h-3.5" /> Reject
          </button>
        </div>
      </td>
    </tr>
  );
}

export default function AdminPage() {
  const { user, loading } = useAuth();
  const [stats, setStats] = useState({});
  const [overview, setOverview] = useState({ leads: [], bids: [], pending_dmcs: [], pending_count: 0, verified_count: 0 });
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get("/admin/stats").then((r) => setStats(r.data || {})).catch(() => {});
    api.get("/admin/overview").then((r) => setOverview(r.data || {})).catch(() => {});
  };

  useEffect(() => {
    if (user?.role === "admin") load();
  }, [user]);

  // Live: when any new bid arrives, refresh overview + toast
  useBidStream(user?.role === "admin" ? "all" : null, (evt) => {
    toast.success(`New bid · $${Number(evt.data?.amount_usd || 0).toLocaleString()} from ${evt.data?.dmc_name}`);
    load();
  });

  if (loading) return <div className="App"><Navbar /><div className="max-w-7xl mx-auto px-6 py-20 text-[#a3a3a3]">Loading…</div></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "admin") return <Navigate to="/dashboard" replace />;

  const verifyDmc = async (dmc) => {
    setBusy(true);
    try {
      await api.post(`/admin/dmcs/${dmc.user_id}/verify`);
      toast.success(`Approved · ${dmc.name}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to approve");
    } finally { setBusy(false); }
  };

  const rejectDmc = async (dmc) => {
    if (!confirm(`Reject ${dmc.name}? They will not appear in the network.`)) return;
    setBusy(true);
    try {
      await api.post(`/admin/dmcs/${dmc.user_id}/reject`);
      toast.success(`Rejected · ${dmc.name}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to reject");
    } finally { setBusy(false); }
  };

  const togglePremium = async (uid, name) => {
    try {
      const r = await api.post(`/admin/dmcs/${uid}/premium`);
      toast.success(`${name} → ${r.data.premium ? "Premium" : "Standard"}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="App" data-testid="admin-page">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 py-10">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 flex-wrap mb-8">
          <div>
            <p className="overline flex items-center gap-2">
              <ShieldCheck className="w-3.5 h-3.5" /> Admin · Platform Console
            </p>
            <h1 className="font-display text-4xl md:text-5xl font-black tracking-tighter text-white mt-3 uppercase">
              Network <span className="text-brand">overview</span>
            </h1>
            <p className="text-sm text-[#a3a3a3] mt-2">
              Live bid stream · DMC verification queue · platform health.
            </p>
          </div>
          <div className="inline-flex items-center gap-2 bg-[#0a0a0a] border border-[#262626] rounded-md px-3 py-2 text-xs font-mono text-[#a3a3a3]" data-testid="live-indicator">
            <span className="w-2 h-2 rounded-full bg-brand animate-pulse" /> LIVE · listening
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatTile label="DMCs" value={stats.dmcs ?? "—"} hint={`${overview.verified_count} verified`} icon={<Building2 className="w-4 h-4" />} />
          <StatTile label="Travel Agents" value={stats.agents ?? "—"} hint="active" icon={<Users className="w-4 h-4" />} />
          <StatTile label="Open Leads" value={stats.leads_open ?? "—"} hint={`${stats.leads_awarded ?? 0} awarded`} icon={<FileText className="w-4 h-4" />} />
          <StatTile label="Total Bids" value={stats.bids ?? "—"} hint="across the network" icon={<Gavel className="w-4 h-4" />} />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
          <StatTile label="Premium DMCs" value={stats.premium_dmcs ?? "—"} hint="featured" icon={<Crown className="w-4 h-4" />} />
          <StatTile label="Pending DMCs" value={overview.pending_count ?? "—"} hint="awaiting verification" icon={<ShieldCheck className="w-4 h-4" />} />
          <StatTile label="Escrow Held" value={`$${Math.round(stats.escrow_held_usd || 0).toLocaleString()}`} hint="10% holds" icon={<Activity className="w-4 h-4" />} />
          <StatTile label="Countries" value={stats.countries ?? 42} hint="network reach" icon={<Globe2 className="w-4 h-4" />} />
        </div>

        {/* Verification Queue */}
        <section className="mt-12">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
            <div>
              <p className="overline">Verification Queue</p>
              <h2 className="font-display text-2xl font-bold text-white mt-2">DMCs awaiting approval</h2>
            </div>
            <span className="text-xs font-mono text-[#737373] uppercase tracking-widest">
              {overview.pending_count} pending
            </span>
          </div>
          <div className="bg-[#0a0a0a] border border-[#262626] rounded-xl overflow-hidden" data-testid="verification-queue">
            {overview.pending_dmcs.length === 0 ? (
              <div className="p-12 text-center text-[#737373]">
                <CheckCircle2 className="w-8 h-8 text-brand mx-auto" />
                <p className="font-display text-white text-lg mt-3">Queue is clear.</p>
                <p className="text-sm mt-1">All DMC applications have been processed.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-[#080808] text-left">
                    <tr>
                      <th className="py-3 px-4 font-mono text-[10px] uppercase tracking-widest text-[#737373]">Company</th>
                      <th className="py-3 px-4 font-mono text-[10px] uppercase tracking-widest text-[#737373]">Country</th>
                      <th className="py-3 px-4 font-mono text-[10px] uppercase tracking-widest text-[#737373]">Specialties</th>
                      <th className="py-3 px-4 font-mono text-[10px] uppercase tracking-widest text-[#737373]">Applied</th>
                      <th className="py-3 px-4 font-mono text-[10px] uppercase tracking-widest text-[#737373] text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {overview.pending_dmcs.map((d) => (
                      <PendingDmcRow key={d.user_id} dmc={d} onVerify={verifyDmc} onReject={rejectDmc} />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </section>

        {/* Activity feed */}
        <section className="mt-12 grid lg:grid-cols-2 gap-6">
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display text-xl font-bold text-white flex items-center gap-2">
                <Bell className="w-5 h-5 text-brand" /> Recent Bids
              </h2>
              <span className="text-xs font-mono text-[#737373] uppercase tracking-widest">live</span>
            </div>
            <ul className="bg-[#0a0a0a] border border-[#262626] rounded-xl divide-y divide-[#1a1a1a]" data-testid="recent-bids">
              {overview.bids.length === 0 && (
                <li className="p-6 text-sm text-[#737373]">No bids yet.</li>
              )}
              {overview.bids.map((b) => (
                <li key={b.bid_id} className="p-4 flex items-center justify-between text-sm">
                  <div className="min-w-0 flex-1 pr-3">
                    <p className="text-white font-medium truncate">
                      {b.dmc_name} {b.dmc_premium && <Crown className="inline w-3 h-3 text-brand" />}
                    </p>
                    <p className="text-xs text-[#737373] font-mono truncate">
                      {b.dmc_country || "—"} · {b.timeline_days}d delivery
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-mono text-white font-bold">${Number(b.amount_usd).toLocaleString()}</p>
                    <p className="text-[10px] font-mono text-brand">AI {b.ai_score?.toFixed?.(1) || "—"}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h2 className="font-display text-xl font-bold text-white mb-4 flex items-center gap-2">
              <FileText className="w-5 h-5 text-brand" /> Recent Leads
            </h2>
            <ul className="bg-[#0a0a0a] border border-[#262626] rounded-xl divide-y divide-[#1a1a1a]" data-testid="recent-leads">
              {overview.leads.length === 0 && (
                <li className="p-6 text-sm text-[#737373]">No leads yet.</li>
              )}
              {overview.leads.map((l) => (
                <li key={l.lead_id} className="p-4">
                  <Link to={`/leads/${l.lead_id}`} className="flex items-center justify-between text-sm hover:bg-[#0f0f0f] -m-2 p-2 rounded">
                    <div className="min-w-0 flex-1 pr-3">
                      <p className="text-white font-medium truncate">{l.title}</p>
                      <p className="text-xs text-[#737373] font-mono truncate">
                        {l.destination} · {l.pax} pax · {l.duration_days}d
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-mono text-white font-bold">${Number(l.budget_usd).toLocaleString()}</p>
                      <p className={`text-[10px] font-mono uppercase tracking-widest ${
                        l.status === "open" ? "text-brand" : "text-green-400"
                      }`}>{l.status}</p>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </section>
      </div>
    </div>
  );
}
